/**
 * Every transition at once, without one diagram of everything (burndown Phase 8).
 *
 * Decided 2026-10-20: a single map of every def and every machine's transitions
 * is the requirements hierarchy's thin strip again - unreadable by construction
 * at a normal model's size. So three smaller views, from one read:
 *
 *  - TRANSITIONS: a table of every transition, filtered by def and by machine.
 *    Picking a def focuses it.
 *  - FOCUS: one def's direct ins and outs across every machine that uses it,
 *    labelled by machine. Clicking a neighbour moves the focus one step.
 *  - MACHINES: one box per machine, linked by the defs they share and by what
 *    their states include; a box opens the view paired with that machine.
 */

import { useEffect, useMemo, useState } from 'react'
import '@xyflow/react/dist/style.css'
import { Background, MarkerType, Position, ReactFlow } from '@xyflow/react'
import type { Edge, Node } from '@xyflow/react'
import { useNavigate } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { ApiError, api } from '../../api'
import type { StateMap } from '../../schema'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import { definitionsIn, filterTransitions, focusOf, overviewPlaces } from './stateMap'

const BOX = { width: 240 }

export default function StateMapPanel() {
  const [map, setMap] = useState<StateMap | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [view, setView] = useState<'transitions' | 'machines'>('transitions')
  const [definition, setDefinition] = useState('')
  const [machine, setMachine] = useState('')

  useEffect(() => {
    api.stateMap().then(setMap).catch((failure: ApiError) => setError(failure))
  }, [])

  if (error) return <Alert severity="error">{error.message}</Alert>
  if (!map) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Stack spacing={2}>
      <Tabs value={view} onChange={(_event, next) => setView(next)}>
        <Tab value="transitions" label="Transitions" />
        <Tab value="machines" label="Machines" />
      </Tabs>
      {view === 'transitions' ? (
        <TransitionsView map={map} definition={definition} machine={machine}
          onDefinition={setDefinition} onMachine={setMachine} />
      ) : (
        <MachinesView map={map} />
      )}
    </Stack>
  )
}

function TransitionsView({
  map, definition, machine, onDefinition, onMachine,
}: {
  map: StateMap
  definition: string
  machine: string
  onDefinition: (id: string) => void
  onMachine: (id: string) => void
}) {
  const defs = useMemo(() => definitionsIn(map), [map])
  const rows = filterTransitions(map, { definition, machine })
  const chosenDef = defs.find((entry) => entry.id === definition) ?? null
  const chosenMachine = map.machines.find((entry) => entry.id === machine) ?? null

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Autocomplete
          size="small"
          sx={{ minWidth: 260 }}
          options={defs}
          value={chosenDef}
          getOptionLabel={(entry) => `${entry.name} (${entry.id})`}
          onChange={(_event, entry) => onDefinition(entry?.id ?? '')}
          renderInput={(params) => <TextField {...params} label="State def" />}
        />
        <Autocomplete
          size="small"
          sx={{ minWidth: 260 }}
          options={map.machines}
          value={chosenMachine}
          getOptionLabel={(entry) => `${entry.name} (${entry.id})`}
          onChange={(_event, entry) => onMachine(entry?.id ?? '')}
          renderInput={(params) => <TextField {...params} label="Machine" />}
        />
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
          {rows.length} of {map.transitions.length} transitions
        </Typography>
      </Stack>

      {definition && <FocusDiagram map={map} definition={definition} onFocus={onDefinition} />}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>From</TableCell>
            <TableCell>To</TableCell>
            <TableCell>Trigger</TableCell>
            <TableCell>Guard</TableCell>
            <TableCell>Decided by</TableCell>
            <TableCell>Machine</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((edge, index) => (
            <TableRow key={`${edge.declared_in.id}-${index}`}>
              {[edge.from, edge.to].map((end, side) => (
                <TableCell key={side} sx={{ fontFamily: MONOSPACE }}>
                  {end ? (
                    <Link component="button" onClick={() => onDefinition(end.definition)}
                      title={`Focus ${end.definition_name}`}>
                      {end.id}
                    </Link>
                  ) : (side ? edge.to_reference : edge.from_reference)}
                </TableCell>
              ))}
              <TableCell>{edge.trigger || '-'}</TableCell>
              <TableCell>{edge.guard || '-'}</TableCell>
              <TableCell>{edge.decided_by || '-'}</TableCell>
              <TableCell>{edge.machine.name}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  )
}

/** One def in the middle, what leads into it on the left and out of it on the right. */
function FocusDiagram({ map, definition, onFocus }: { map: StateMap; definition: string; onFocus: (id: string) => void }) {
  const theme = useTheme()
  const { ins, outs } = focusOf(map, definition)
  const centre = definitionsIn(map).find((entry) => entry.id === definition)
  const height = Math.max(ins.length, outs.length, 1) * 80

  const nodes: Node[] = [{
    id: 'focus', position: { x: 340, y: height / 2 - 30 }, data: { label: centre?.name ?? definition },
    sourcePosition: Position.Right, targetPosition: Position.Left,
    style: { ...BOX, fontWeight: 600, borderWidth: 2 },
  }]
  const edges: Edge[] = []
  const side = (entries: typeof ins, x: number, key: 'in' | 'out') =>
    entries.forEach((entry, index) => {
      const id = `${key}-${index}`
      nodes.push({
        id, position: { x, y: index * 80 }, data: { label: `${entry.name} · in ${entry.machine}`, definition: entry.definition },
        sourcePosition: Position.Right, targetPosition: Position.Left, style: BOX,
      })
      edges.push({
        id: `e-${id}`, source: key === 'in' ? id : 'focus', target: key === 'in' ? 'focus' : id,
        label: entry.label || undefined, markerEnd: { type: MarkerType.ArrowClosed },
      })
    })
  side(ins, 0, 'in')
  side(outs, 680, 'out')

  return (
    <Box sx={{ height: height + 80, border: 1, borderColor: 'divider', borderRadius: 1 }} data-testid="state-focus">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodesDraggable={false}
        nodesConnectable={false}
        onNodeClick={(_event, node) => {
          const next = (node.data as { definition?: string }).definition
          if (next) onFocus(next)
        }}
        colorMode={theme.palette.mode}
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <Background />
      </ReactFlow>
    </Box>
  )
}

/** One box per machine, linked by shared defs and includes; a box opens its view. */
function MachinesView({ map }: { map: StateMap }) {
  const theme = useTheme()
  const navigate = useNavigate()
  const toasts = useToasts()
  const places = overviewPlaces(map.machines.length)

  const nodes: Node[] = map.machines.map((entry, index) => ({
    id: entry.id,
    position: places[index],
    data: { label: `${entry.name} — ${entry.defs.length} state${entry.defs.length === 1 ? '' : 's'}${entry.views.length ? '' : ' · no view'}` },
    style: BOX,
  }))
  const edges: Edge[] = map.links.map((link, index) => ({
    id: `l-${index}`,
    source: link.from,
    target: link.to,
    // Short, or it does not fit between two boxes: the names past two are counted.
    label: `${link.kind} ${link.via.length > 2
      ? `${link.via.length} states`
      : link.via.map((name) => name.split('::').pop()).join(', ')}`,
    animated: link.kind === 'includes',
    markerEnd: link.kind === 'includes' ? { type: MarkerType.ArrowClosed } : undefined,
  }))

  if (map.machines.length === 0) {
    return <Typography color="text.secondary">The model has no state machines yet.</Typography>
  }
  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        A line between two machines: they hold usages of the same defs (shares), or a state of one
        includes a state of the other (includes, arrowed). Select a machine to open its diagram.
      </Typography>
      <Box sx={{ height: 480, border: 1, borderColor: 'divider', borderRadius: 1 }} data-testid="machine-overview">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodesConnectable={false}
          onNodeClick={(_event, node) => {
            const found = map.machines.find((entry) => entry.id === node.id)
            if (found?.views.length) navigate(`/views?view=${encodeURIComponent(found.views[0])}`)
            else toasts.notify(`${found?.name ?? node.id} has no view of its own yet: Views → New view → State diagram of an existing machine.`, 'info')
          }}
          colorMode={theme.palette.mode}
          fitView
          proOptions={{ hideAttribution: true }}
        >
          <Background />
        </ReactFlow>
      </Box>
    </Stack>
  )
}
